import { Observable, Subscription } from 'rxjs'
import { shareReplay, take, tap } from 'rxjs/operators'

import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core'
import { Router } from '@angular/router'
import { FileExtension, FileReference } from '@classes/FileReference'
import { AssetService, SearchObservationRef, FileDetails, FileDetailsFeature, FileDetailsFeatureScene, FileDetailsRelatedAsset } from '@services/asset.service'
import { FileReferenceService } from '@services/file-reference.service'
import { RightSidebarService } from '@services/right-sidebar.service'
import { SearchNavigationService } from '@services/search-navigation.service'
import { SearchService } from '@services/search.service'

@Component({
    selector: 'app-file-details-panel',
    templateUrl: './file-details-panel.component.html',
    styleUrls: ['./file-details-panel.component.css'],
    standalone: false
})
export class FileDetailsPanelComponent implements OnChanges, OnDestroy {
    @Input() assetId: number | null = null
    @Input() fileId: number | null = null
    @Input() previewUrl: Observable<string> | null = null
    @Input() s3Id: number | null = null
    @Input() searchObservation?: SearchObservationRef
    @Output() closed = new EventEmitter<void>()

    fileDetails: FileDetails | null = null
    imageUrl$: Observable<string> | null = null
    loading = false
    errorMessage: string | null = null
    fileOpenError: string | null = null
    showAiDescription = false

    private _detailsSubscription?: Subscription
    private _openFileSubscription?: Subscription
    private _objectUrls = new Set<string>()
    private _pendingFileWindow: Window | null = null

    constructor(
        private _assetService: AssetService,
        private _fileReferenceService: FileReferenceService,
        private _rightSidebarService: RightSidebarService,
        private _router: Router,
        private _searchNavigationService: SearchNavigationService,
        private _searchService: SearchService
    ) { }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['searchObservation'] || (changes['fileId'] && this.fileId) || (changes['s3Id'] && this.s3Id)) {
            this.loadFileDetails()
        }
    }

    private loadFileDetails(): void {
        this._detailsSubscription?.unsubscribe()
        this.cancelPendingFileOpen()
        this.fileOpenError = null
        this.revokeObjectUrls()
        this.fileDetails = null
        this.imageUrl$ = null
        this.errorMessage = null
        this.loading = true

        const details$ = this.s3Id
            ? this._assetService.getS3FileDetails(this.s3Id, this.searchObservation)
            : this.fileId
                ? this._assetService.getFileDetails(this.fileId, this.searchObservation)
                : null

        if (!details$) {
            this.loading = false
            this.errorMessage = 'Unable to load file details.'
            return
        }

        this._detailsSubscription = details$.pipe(take(1)).subscribe({
            next: details => {
                this.fileDetails = details
                this.loading = false

                if (this.isImageFile(details.extension)) {
                    if (this.previewUrl) {
                        this.imageUrl$ = this.previewUrl
                    } else if (details.s3ObjectReferenceId) {
                        this.imageUrl$ = this._fileReferenceService.getS3PresignedUrl(details.s3ObjectReferenceId)
                    } else {
                        if (details.fileId == null) {
                            this.errorMessage = 'Unable to load file details. Please try again.'
                            this.fileDetails = null
                            return
                        }
                        const assetId = this.assetId ?? details.relatedAssets?.[0]?.assetId ?? 0
                        const fileRef = new FileReference(details.fileId, undefined, undefined, {
                            hash: details.hash
                        })
                        this.imageUrl$ = this.trackObjectUrl(
                            this._fileReferenceService.getBlobUrlForFileReference(fileRef, assetId, 'asset')
                        )
                    }
                }
            },
            error: () => {
                this.loading = false
                this.errorMessage = 'Unable to load file details. Please try again.'
            }
        })
    }

    retry(): void {
        this.loadFileDetails()
    }

    private trackObjectUrl(source$: Observable<string>): Observable<string> {
        return source$.pipe(
            tap(url => this._objectUrls.add(url)),
            shareReplay({ bufferSize: 1, refCount: true })
        )
    }

    private revokeObjectUrls(): void {
        this._objectUrls.forEach(url => URL.revokeObjectURL(url))
        this._objectUrls.clear()
    }

    close(): void {
        this.closed.emit()
    }

    toggleAiDescription(): void {
        this.showAiDescription = !this.showAiDescription
    }

    private isValidFileExtension(ext: string): ext is FileExtension {
        const validExtensions: FileExtension[] = [
            '.3dm', '.bin', '.dae', '.drc', '.fbx', '.glb', '.gltf', '.jpg', '.js',
            '.json', '.mtl', '.obj', '.ogg', '.pcd', '.pdb', '.pmd', '.png', '.prwm',
            '.svg', '.tga', '.txt', '.pdf', '.e57', '.las', '.laz', '.ksplat',
            '.splat', '.ply', '.spz', '.stl'
        ]
        return validExtensions.includes(ext as FileExtension)
    }

    openFile(fileDetails: FileDetails): void {
        this.fileOpenError = null
        // S3-backed files: download via presigned URL
        if (fileDetails.s3ObjectReferenceId) {
            this._fileReferenceService.downloadS3File(
                fileDetails.s3ObjectReferenceId,
                fileDetails.filename
            ).subscribe({
                error: err => console.error('Failed to download S3 file:', err)
            })
            return
        }

        if (fileDetails.fileId == null) return

        const extension = this.isValidFileExtension(fileDetails.extension)
            ? fileDetails.extension
            : '.bin' // fallback to a safe default

        const fileRef = new FileReference(
            fileDetails.fileId,
            undefined, // modelId - not available in FileDetails
            undefined, // file - not needed for opening existing file
            {
                hash: fileDetails.hash,
                filename: fileDetails.filename,
                extension: extension,
                size: fileDetails.size,
                label: fileDetails.label
            }
        )

        const assetId = this.assetId ?? fileDetails.relatedAssets?.[0]?.assetId ?? 0

        if (extension === '.pdf') {
            this.openAuthenticatedFileInNewTab(fileRef, assetId)
            return
        }

        this._fileReferenceService.openFile(fileRef, assetId, 'asset').subscribe()
    }

    private cancelPendingFileOpen(): void {
        this._openFileSubscription?.unsubscribe()
        this._pendingFileWindow?.close()
        this._pendingFileWindow = null
    }

    private openAuthenticatedFileInNewTab(fileReference: FileReference, assetId: number): void {
        this.cancelPendingFileOpen()

        const fileWindow = window.open('', '_blank')
        if (!fileWindow) {
            this.fileOpenError = 'Allow pop-ups for Kartorium, then try opening this file again.'
            return
        }

        this._pendingFileWindow = fileWindow
        fileWindow.document.title = fileReference.filename || 'Loading file'
        fileWindow.document.body.textContent = 'Loading file...'

        this._openFileSubscription = this._fileReferenceService
            .getBlobUrlForFileReference(fileReference, assetId, 'asset')
            .pipe(take(1))
            .subscribe({
                next: blobUrl => {
                    if (fileWindow.closed) {
                        URL.revokeObjectURL(blobUrl)
                        return
                    }

                    const frame = fileWindow.document.createElement('iframe')
                    frame.src = blobUrl
                    frame.title = fileReference.filename || 'File preview'
                    frame.style.border = '0'
                    frame.style.height = '100vh'
                    frame.style.width = '100%'

                    let blobUrlReleased = false
                    const releaseBlobUrl = () => {
                        if (blobUrlReleased) return
                        blobUrlReleased = true
                        URL.revokeObjectURL(blobUrl)
                        fileWindow.removeEventListener('beforeunload', releaseBlobUrl)
                        fileWindow.removeEventListener('pagehide', releaseBlobUrl)
                    }
                    fileWindow.addEventListener('beforeunload', releaseBlobUrl)
                    fileWindow.addEventListener('pagehide', releaseBlobUrl)

                    fileWindow.document.body.replaceChildren(frame)
                    fileWindow.document.body.style.margin = '0'
                },
                error: () => {
                    fileWindow.close()
                    this._pendingFileWindow = null
                    this.fileOpenError = 'Unable to open this file. It may be unavailable or you may not have access. Try opening it again.'
                },
                complete: () => {
                    this._pendingFileWindow = null
                }
            })
    }

    onAssetClick(event: Event, assetId: number, assetTypeId: number): void {
        event.preventDefault()

        // Save search context with current file ID
        const currentQuery = this._searchService.lastQuery
        this._searchNavigationService.saveSearchContext(currentQuery, this.fileId)

        // Navigate to the asset
        this._router.navigate(['/assets', assetTypeId], {
            queryParams: { assetID: assetId, fromSearch: 'true' },
            queryParamsHandling: 'merge'
        }).then(() => {
            // Load and open the asset in the edit panel
            this._assetService.setSelectedAssetByID(assetId).pipe(take(1)).subscribe({
                next: () => {
                    this._rightSidebarService.openPanel('Edit Asset')
                    // Close the file details panel after the Edit Asset panel opens
                    this.close()
                },
                error: () => {
                    this.close()
                }
            })
        })
    }

    onFeatureClick(event: Event, projectId: number, sceneId: number, featureId: number): void {
        // Only prevent default for left clicks - let middle/right clicks work naturally
        if ((event as MouseEvent).button === 0) {
            event.preventDefault()

            // Build the URL
            const url = this._router.serializeUrl(
                this._router.createUrlTree(['/projects', projectId], {
                    queryParams: { scene: sceneId, feature: featureId }
                })
            )

            // Open in new tab
            window.open(url, '_blank')
        }
    }

    getFeatureUrl(projectId: number, sceneId: number, featureId: number): string {
        return this._router.serializeUrl(
            this._router.createUrlTree(['/projects', projectId], {
                queryParams: { scene: sceneId, feature: featureId }
            })
        )
    }

    // Helper method to get all unique scenes from an asset's features
    getScenes(asset: FileDetailsRelatedAsset): FileDetailsFeatureScene[] {
        if (!asset.features || !asset.features.length) {
            return []
        }

        const scenesMap = new Map<number, FileDetailsFeatureScene>()
        asset.features.forEach((feature) => {
            if (feature.scenes && feature.scenes.length) {
                feature.scenes.forEach((scene) => {
                    if (!scenesMap.has(scene.sceneId)) {
                        scenesMap.set(scene.sceneId, scene)
                    }
                })
            }
        })

        return Array.from(scenesMap.values())
    }

    // Helper method to get features for a specific scene
    getFeaturesForScene(asset: FileDetailsRelatedAsset, sceneId: number): FileDetailsFeature[] {
        if (!asset.features || !asset.features.length) {
            return []
        }

        return asset.features.filter((feature) => {
            return feature.scenes && feature.scenes.some((scene) => scene.sceneId === sceneId)
        })
    }

    formatFileSize(bytes: number): string {
        if (bytes === 0) return '0 Bytes'
        const k = 1024
        const sizes = ['Bytes', 'KB', 'MB', 'GB']
        const i = Math.floor(Math.log(bytes) / Math.log(k))
        return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i]
    }

    getFileIcon(extension: string): string {
        const ext = extension?.toLowerCase() || ''

        if (['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg', '.bmp'].includes(ext)) {
            return 'fa-image'
        } else if (['.pdf'].includes(ext)) {
            return 'fa-file-pdf'
        } else if (['.doc', '.docx'].includes(ext)) {
            return 'fa-file-word'
        } else if (['.xls', '.xlsx'].includes(ext)) {
            return 'fa-file-excel'
        } else if (['.mp4', '.mov', '.avi', '.webm'].includes(ext)) {
            return 'fa-file-video'
        } else if (['.mp3', '.wav', '.ogg'].includes(ext)) {
            return 'fa-file-audio'
        } else if (['.zip', '.rar', '.7z'].includes(ext)) {
            return 'fa-file-zipper'
        } else if (['.glb', '.gltf', '.obj', '.fbx'].includes(ext)) {
            return 'fa-cube'
        } else {
            return 'fa-file'
        }
    }

    getFileTypeLabel(extension: string): string {
        const ext = extension?.toLowerCase() || ''

        if (['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg', '.bmp'].includes(ext)) {
            return 'IMAGE'
        } else if (['.pdf'].includes(ext)) {
            return 'PDF'
        } else if (['.doc', '.docx'].includes(ext)) {
            return 'DOC'
        } else if (['.xls', '.xlsx'].includes(ext)) {
            return 'EXCEL'
        } else if (['.mp4', '.mov', '.avi', '.webm'].includes(ext)) {
            return 'VIDEO'
        } else if (['.mp3', '.wav', '.ogg'].includes(ext)) {
            return 'AUDIO'
        } else if (['.zip', '.rar', '.7z'].includes(ext)) {
            return 'ARCHIVE'
        } else if (['.glb', '.gltf', '.obj', '.fbx'].includes(ext)) {
            return '3D'
        } else {
            return 'FILE'
        }
    }

    isImageFile(extension: string): boolean {
        const ext = extension?.toLowerCase() || ''
        return ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg', '.bmp'].includes(ext)
    }

    ngOnDestroy(): void {
        this._detailsSubscription?.unsubscribe()
        this.cancelPendingFileOpen()
        this.revokeObjectUrls()
    }
}
